'use client';

import type { useApiMutation } from '@shared/api-utils';
import { Button, Label, RadioGroup, RadioGroupItem } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { Spinner } from '@shared/icons';
import { Reminder } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { FieldMessage } from '../../../../../form-controls/FieldMessage/FieldMessage';
import { useMultistepForm } from '../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { REALITY_CHECK_INTERVALS } from '../utils/realityCheckIntervals';

type SetupStepProps = {
  mutation: ReturnType<typeof useApiMutation<{ success: boolean; message: string; value: string }, { value: string }>>;
};

const SetupStep = ({ mutation }: SetupStepProps) => {
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations('my_account.safer_gambling.reality_check');
  const form = useTranslations('form');
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const { goToNextStep, isLoading } = useMultistepForm();

  const intervals = REALITY_CHECK_INTERVALS.map(value => ({
    value,
    label: t(`intervals.${value}`),
  }));

  const selectedInterval = watch('interval');

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({ value: selectedInterval });
      await goToNextStep();
    } catch (error) {
      // Error is handled by the mutation's onError callback
      debug.forceError('Error saving reality check settings:', error);
    }
  };

  const isButtonLoading = isLoading || mutation.isPending;

  return (
    <div className="flex flex-col gap-8 px-6 py-0" data-track-container="Reality Check Set" data-testid="reality-check-sheet">
      {/* Illustrative Message Section */}
      <div className="flex flex-col gap-5 items-start justify-center w-full mt-6">
        <Reminder size="extra-extra-large" data-testid="reminder-icon" />
        <div className="flex flex-col gap-2 items-start justify-start w-full">
          <h4>{t('steps.setup.title')}</h4>
          <p className=" w-full">{t('steps.setup.description')}</p>
        </div>
      </div>

      {/* Radio Group */}
      <div className="flex flex-col gap-4 items-start justify-start w-full">
        <RadioGroup value={selectedInterval} onValueChange={value => setValue('interval', value)} className="flex flex-col gap-4 w-full" data-track-name="Period">
          {intervals.map(interval => (
            <div key={interval.value} className="flex flex-row gap-3 h-10 items-center justify-start w-full">
              <RadioGroupItem className="size-7" value={interval.value} id={`interval-${interval.value}`} data-testid={`interval-${interval.value}`} />
              <Label className="paragraph-explanatory hover:text-inherit" htmlFor={`interval-${interval.value}`}>
                {interval.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <FieldMessage message={errors.interval?.message?.toString()} messageType="error" />
      </div>

      {/* Button */}
      <div className="flex flex-col gap-2.5 items-center justify-end w-full mt-auto" data-track-container="Actions" data-testid="reality-check-submit-button">
        <Button className="w-full" onClick={handleSave} disabled={!selectedInterval || isButtonLoading} data-track-name="Confirm">
          {isButtonLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={form('aria.loading')} /> : t('steps.setup.save')}
        </Button>
      </div>
    </div>
  );
};

export { SetupStep };
